import { useEffect, useRef, useState } from "react";
import { MessageSquareText, Search } from "lucide-react";
import type { Route, RouteTarget } from "../lib/useRoute.js";
import { NotificationsBell } from "../components/NotificationsBell.js";
import { useSession, signOut } from "../lib/auth-client.js";

/**
 * Sticky top bar. Hosts the page title, a ⌘K search trigger, the notifications
 * bell, the AI chat trigger, and the signed-in user's menu.
 */

const TITLES: Record<Route, { title: string; subtitle: string }> = {
  video: { title: "Video", subtitle: "What is waiting for footage, what is running, what came out, and what can be reused" },
  automations: { title: "Automations", subtitle: "What runs without you — schedules, last runs, and what is still manual" },
  models: { title: "Models", subtitle: "Which model does which job, and what each one costs" },
  offerings: { title: "Fund Offerings", subtitle: "What the health funds publish — their catalogue, prices and limits, with a source and a date" },
  folded: { title: "Folded Variants", subtitle: "Spelling variants the triage merged automatically — split back what shouldn't share a page" },
  overview: { title: "Overview", subtitle: "Pipeline health + recent activity" },
  tasks: { title: "Tasks", subtitle: "Pending actions across the content lifecycle" },
  sources: { title: "Sources", subtitle: "Discover new content via URL analysis + RSS feeds" },
  research: { title: "Keyword Research", subtitle: "Search volume + difficulty + intent, kept and reused" },
  pipeline: { title: "Pipeline", subtitle: "Keywords + clusters in flight" },
  content: { title: "Articles", subtitle: "Produced article catalogue" },
  "content-db": { title: "Content database", subtitle: "Live Sanity mirror + internal-link source" },
  entities: { title: "Entities", subtitle: "Competitors, partners, roster + do-not-mention guardrail" },
  calendar: { title: "Calendar", subtitle: "What goes out, and when — Israel time" },
  distribution: { title: "Distribution", subtitle: "Sends, schedules, campaigns" },
  audience: { title: "Audience", subtitle: "Contacts, lists, segments" },
  audit: { title: "Site Audit", subtitle: "Live-site SEO/health crawl + findings" },
  credentials: { title: "Credentials", subtitle: "API keys + integration health" },
  errors: { title: "Errors", subtitle: "Pipeline failures + warnings — central log" },
  spend: { title: "Spend", subtitle: "Per-call cost across every paid provider" },
  brand: { title: "Brand kit", subtitle: "Voice, identity, hashtags, assets — single source of truth" },
  help: { title: "Help center", subtitle: "Workflow guides, troubleshooting, glossary" },
};

function UserMenu() {
  const { data } = useSession();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const user = data?.user;
  const initial = (user?.name || user?.email || "?").charAt(0).toUpperCase();

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [open]);

  return (
    <div ref={ref} className="relative ml-1">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full bg-brand-50 text-sm font-semibold text-brand-800 ring-1 ring-inset ring-brand/15"
        title={user?.email ?? "Account"}
      >
        {user?.image ? (
          <img src={user.image} alt="" className="h-full w-full object-cover" />
        ) : (
          initial
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-11 z-50 w-56 overflow-hidden rounded-xl border border-line bg-white shadow-xl">
          <div className="border-b border-line px-4 py-3">
            <p className="truncate text-sm font-medium text-ink">{user?.name ?? "Signed in"}</p>
            <p className="truncate text-xs text-muted">{user?.email}</p>
          </div>
          <button
            onClick={() => {
              void signOut().then(() => window.location.reload());
            }}
            className="w-full px-4 py-2.5 text-left text-sm text-ink hover:bg-line/40"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

export function Topbar({
  route,
  onOpenChat,
  onOpenPalette,
  onNavigate,
}: {
  route: Route;
  onOpenChat: () => void;
  onOpenPalette: () => void;
  onNavigate: (r: RouteTarget) => void;
}) {
  const meta = TITLES[route];
  return (
    <header className="sticky top-0 z-30 flex h-16 items-center justify-between gap-4 border-b border-line bg-white/80 px-6 backdrop-blur supports-[backdrop-filter]:bg-white/70">
      <div className="min-w-0">
        <h1 className="truncate text-lg font-semibold text-ink">{meta.title}</h1>
        <p className="hidden truncate text-xs text-muted lg:block">{meta.subtitle}</p>
      </div>

      <div className="flex items-center gap-2">
        {/* Search — opens the ⌘K command palette. */}
        <button
          onClick={onOpenPalette}
          className="relative hidden items-center gap-2 rounded-lg border border-line bg-white py-2 pl-9 pr-3 text-sm text-muted shadow-sm transition hover:bg-line/20 md:flex md:w-64 lg:w-80"
        >
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
          <span className="flex-1 text-left">Search…</span>
          <kbd className="rounded border border-line bg-canvas px-1.5 py-0.5 text-2xs text-muted">⌘K</kbd>
        </button>

        <NotificationsBell onNavigate={onNavigate} />

        <button onClick={onOpenChat} className="btn-icon" title="AlternaBe AI (⌘J)">
          <MessageSquareText className="h-4 w-4" />
        </button>

        <UserMenu />
      </div>
    </header>
  );
}
